/**
 * @engine/vfx - Node Registry
 *
 * Registry for VFX node definitions.
 *
 * Extends the generic {@link BaseRegistry} (@engine/graph) — which provides
 * Map storage, the category + keyword indexes, and scored `search()` — and
 * adds only the VFX specifics: idempotent registration and a per-context
 * index (`getByContext` / `canUseInContext`).
 */

import { BaseRegistry } from '@engine/graph';
import type { VFXNodeDefinition, VFXContextType } from './types';

/** Contexts a context-less node (operators, etc.) is available in. */
const ALL_CONTEXTS: readonly VFXContextType[] = ['event', 'spawn', 'initialize', 'update', 'output'];

/**
 * VFX node registry.
 */
export class VFXNodeRegistry extends BaseRegistry<VFXNodeDefinition> {
  /**
   * Idempotent registration (VFX node packs import each other and re-register
   * shared definitions). Skip-if-exists now lives in {@link BaseRegistry} via
   * `conflictPolicy`; this class only adds the per-context index below.
   */
  protected override readonly conflictPolicy = 'skip' as const;

  /** Domain index: context → nodes usable there. Not part of BaseRegistry. */
  private readonly _byContext = new Map<VFXContextType, VFXNodeDefinition[]>();

  /**
   * Register a node definition and maintain the per-context index.
   * The base honors `conflictPolicy='skip'`, so we only index genuinely-new ids.
   */
  override register(def: VFXNodeDefinition): void {
    const isNew = !this.has(def.id);
    super.register(def);
    if (!isNew) return;

    // Index by context. Empty contexts = available everywhere (operators).
    const contexts = def.contexts.length === 0 ? ALL_CONTEXTS : def.contexts;
    for (const ctx of contexts) {
      const list = this._byContext.get(ctx) ?? [];
      list.push(def);
      this._byContext.set(ctx, list);
    }
  }

  /**
   * Get all nodes available in a context.
   */
  getByContext(context: VFXContextType): readonly VFXNodeDefinition[] {
    return this._byContext.get(context) ?? [];
  }

  /**
   * Check if a node can be used in a context.
   */
  canUseInContext(nodeId: string, context: VFXContextType): boolean {
    const def = this.get(nodeId);
    if (!def) return false;
    return def.contexts.length === 0 || def.contexts.includes(context);
  }

  /**
   * Clear all registrations (including the context index).
   */
  override clear(): void {
    super.clear();
    this._byContext.clear();
  }
}

/**
 * Global VFX node registry instance.
 */
export const vfxNodeRegistry = new VFXNodeRegistry();
